import { logEvent, setAnalyticsCollectionEnabled, setUserId, setUserProperties } from "firebase/analytics";
import { makeAutoObservable } from "mobx";
import { isCloudEnv } from "./config/env.ts";
import { firebaseAnalytics } from "./config/firebase.ts";
import { getSelectedOrganization } from "./helpers/localStorage.ts";
import { IRootStore } from "./mobx/root-store.tsx";
import { ImageToImageConfigType, PlaygroundImage } from "./models/image-to-image.ts";
import User from "./models/user.ts";
import iframeStore from "./pages/IframeNew/iframe-store.tsx";
import CrmService from "./services/CrmService.ts";
import { isMigrationIframe } from "./utils/migrationIframe.ts";

export enum ANALYTICS_EVENTS {
	PAGE_VIEW = "page_view",
	REGISTER = "REGISTER",
	LOGIN = "LOGIN",
	GET_API_KEY_BUTTON_CLICKED = "GET_API_KEY_BUTTON_CLICKED",
	IMAGE_UPLOAD_ITI = "IMAGE_UPLOAD_ITI",
	IMAGE_DOWNLOAD_ITI = "IMAGE_DOWNLOAD_ITI",
	IMAGE_ERROR_ITI = "IMAGE_ERROR_ITI",
	IMAGE_COMPARE_ITI = "IMAGE_COMPARE_ITI",
	PLAYGROUND_IMAGE_DOWNLOAD_ITI = "PLAYGROUND_IMAGE_DOWNLOAD_ITI",
	PLAYGROUND_IMAGE_UPLOAD = "PLAYGROUND_IMAGE_UPLOAD",
	PLAYGROUND_IMAGE_SAVE_ITI = "PLAYGROUND_IMAGE_SAVE_ITI",
	ITI_GENERATE = "ITI_GENERATE",
	ITI_MIX_FACE_BODY = "ITI_MIX_FACE_BODY",
	ITI_REFINE_IMAGE = "ITI_REFINE_IMAGE",
	ITI_COLOR_REFINE_IMAGE = "ITI_COLOR_REFINE_IMAGE",
	IMAGE_DOWNLOAD = "IMAGE_DOWNLOAD",
	IMAGE_UPLOAD_PP = "IMAGE_UPLOAD_PP",
	IMAGE_DOWNLOAD_PP = "IMAGE_DOWNLOAD_PP",
	PP_GENERATE = "PP_GENERATE",
	MANUAL_PP_GENERATE = "MANUAL_PP_GENERATE",
	IMAGE_GENERATION_GENERATE = "IMAGE_GENERATION_GENERATE",
	IMAGE_GENERATION_BRIA_BASE = "IMAGE_GENERATION_BRIA_BASE",
	IMAGE_GENERATION_BRIA_FAST = "IMAGE_GENERATION_BRIA_FAST",
	IMAGE_GENERATION_BRIA_HD = "IMAGE_GENERATION_BRIA_HD",
	IMAGE_GENERATION_TAILORED_MODELS = "IMAGE_GENERATION_TAILORED_MODELS",
	IMAGE_GENERATION_IMAGE_REFERENCE = "IMAGE_GENERATION_IMAGE_REFERENCE",
	IMAGE_GENERATION_DOWNLOAD_GENERAL = "IMAGE_GENERATION_DOWNLOAD_GENERAL",
	IMAGE_GENERATION_DOWNLOAD_BRIA_BASE = "IMAGE_GENERATION_DOWNLOAD_BRIA_BASE",
	IMAGE_GENERATION_DOWNLOAD_BRIA_FAST = "IMAGE_GENERATION_DOWNLOAD_BRIA_FAST",
	IMAGE_GENERATION_DOWNLOAD_BRIA_HD = "IMAGE_GENERATION_DOWNLOAD_BRIA_HD",
	IMAGE_GENERATION_DOWNLOAD_TAILORED_MODELS = "IMAGE_GENERATION_DOWNLOAD_TAILORED_MODELS",
	IMAGE_GENERATION_DOWNLOAD_IMAGE_REFERENCE = "IMAGE_GENERATION_DOWNLOAD_IMAGE_REFERENCE",
	IMAGE_GENERATION_INCREASE_RESOLUTION = "IMAGE_GENERATION_INCREASE_RESOLUTION",
	IMAGE_EDITING_GENERATE = "IMAGE_EDITING_GENERATE",
	IMAGE_EDITING_REMOVE_BACKGROUND = "IMAGE_EDITING_REMOVE_BACKGROUND",
	IMAGE_EDITING_EXPAND_IMAGE = "IMAGE_EDITING_EXPAND_IMAGE",
	IMAGE_EDITING_CROP_IMAGE = "IMAGE_EDITING_CROP_IMAGE",
	IMAGE_EDITING_BLUR_BACKGROUND = "IMAGE_EDITING_BLUR_BACKGROUND",
	IMAGE_EDITING_SOLID_BACKGROUND = "IMAGE_EDITING_SOLID_BACKGROUND",
	IMAGE_EDITING_GENERATE_BACKGROUND_BY_TEXT = "IMAGE_EDITING_GENERATE_BACKGROUND_BY_TEXT",
	IMAGE_EDITING_GENERATE_BACKGROUND_BY_IMAGE = "IMAGE_EDITING_GENERATE_BACKGROUND_BY_IMAGE",
	IMAGE_EDITING_ASPECT_RATIO = "IMAGE_EDITING_ASPECT_RATIO",
	IMAGE_EDITING_CUSTOM_SIZE = "IMAGE_EDITING_CUSTOM_SIZE",
	IMAGE_EDITING_MEDIUM_DESTINATION = "IMAGE_EDITING_MEDIUM_DESTINATION",
	IMAGE_EDITING_GENERATE_FOREGROUND = "IMAGE_EDITING_GENERATE_FOREGROUND",
	IMAGE_EDITING_DOWNLOAD_GENERAL = "IMAGE_EDITING_DOWNLOAD_GENERAL",
	IMAGE_EDITING_DOWNLOAD_REMOVE_BACKGROUND = "IMAGE_EDITING_DOWNLOAD_REMOVE_BACKGROUND",
	IMAGE_EDITING_DOWNLOAD_EXPAND_IMAGE = "IMAGE_EDITING_DOWNLOAD_EXPAND_IMAGE",
	IMAGE_EDITING_DOWNLOAD_BLUR_BACKGROUND = "IMAGE_EDITING_DOWNLOAD_BLUR_BACKGROUND",
	IMAGE_EDITING_DOWNLOAD_SOLID_BACKGROUND = "IMAGE_EDITING_DOWNLOAD_SOLID_BACKGROUND",
	IMAGE_EDITING_DOWNLOAD_GENERATE_BACKGROUND_BY_TEXT = "IMAGE_EDITING_DOWNLOAD_GENERATE_BACKGROUND_BY_TEXT",
	IMAGE_EDITING_DOWNLOAD_GENERATE_SIZE = "IMAGE_EDITING_DOWNLOAD_GENERATE_SIZE",
	IMAGE_EDITING_DOWNLOAD_ASPECT_RATIO = "IMAGE_EDITING_DOWNLOAD_ASPECT_RATIO",
	IMAGE_EDITING_DOWNLOAD_CUSTOM_SIZE = "IMAGE_EDITING_DOWNLOAD_CUSTOM_SIZE",
	IMAGE_EDITING_DOWNLOAD_MEDIUM_DESTINATION = "IMAGE_EDITING_DOWNLOAD_MEDIUM_DESTINATION",
	IMAGE_EDITING_DOWNLOAD_FOREGROUND = "IMAGE_EDITING_DOWNLOAD_FOREGROUND",
	IMAGE_EDITING_INCREASE_RESOLUTION = "IMAGE_EDITING_INCREASE_RESOLUTION",
	IMAGE_EDITING_ERASE = "IMAGE_EDITING_ERASE",
	IMAGE_EDITING_GENERATIVE_FILL = "IMAGE_EDITING_GENERATIVE_FILL",
	CREATE_NEW_TEMPLATE = "CREATE_NEW_TEMPLATE",
	EXPORT_CAMPAIGN = "EXPORT_CAMPAIGN",
	GO_TO_ADOBE_MARKETPLACE = "GO_TO_ADOBE_MARKETPLACE",
	AUTOMOTIVE_GENERATE = "AUTOMOTIVE_GENERATE",
	AUTOMOTIVE_REMOVE_BACKGROUND = "AUTOMOTIVE_REMOVE_BACKGROUND",
	AUTOMOTIVE_EXPAND_IMAGE = "AUTOMOTIVE_EXPAND_IMAGE",
	AUTOMOTIVE_BLUR_BACKGROUND = "AUTOMOTIVE_BLUR_BACKGROUND",
	AUTOMOTIVE_SOLID_BACKGROUND = "AUTOMOTIVE_SOLID_BACKGROUND",
	AUTOMOTIVE_GENERATE_BACKGROUND_BY_TEXT = "AUTOMOTIVE_GENERATE_BACKGROUND_BY_TEXT",
	AUTOMOTIVE_GENERATE_BACKGROUND_BY_IMAGE = "AUTOMOTIVE_GENERATE_BACKGROUND_BY_IMAGE",
	AUTOMOTIVE_ASPECT_RATIO = "AUTOMOTIVE_ASPECT_RATIO",
	AUTOMOTIVE_CUSTOM_SIZE = "AUTOMOTIVE_CUSTOM_SIZE",
	AUTOMOTIVE_MEDIUM_DESTINATION = "AUTOMOTIVE_MEDIUM_DESTINATION",
	AUTOMOTIVE_GENERATE_FOREGROUND = "AUTOMOTIVE_GENERATE_FOREGROUND",
	AUTOMOTIVE_DOWNLOAD_GENERAL = "AUTOMOTIVE_DOWNLOAD_GENERAL",
	AUTOMOTIVE_DOWNLOAD_REMOVE_BACKGROUND = "AUTOMOTIVE_DOWNLOAD_REMOVE_BACKGROUND",
	AUTOMOTIVE_DOWNLOAD_EXPAND_IMAGE = "AUTOMOTIVE_DOWNLOAD_EXPAND_IMAGE",
	AUTOMOTIVE_DOWNLOAD_BLUR_BACKGROUND = "AUTOMOTIVE_DOWNLOAD_BLUR_BACKGROUND",
	AUTOMOTIVE_DOWNLOAD_SOLID_BACKGROUND = "AUTOMOTIVE_DOWNLOAD_SOLID_BACKGROUND",
	AUTOMOTIVE_DOWNLOAD_GENERATE_BACKGROUND_BY_TEXT = "AUTOMOTIVE_DOWNLOAD_GENERATE_BACKGROUND_BY_TEXT",
	AUTOMOTIVE_DOWNLOAD_GENERATE_SIZE = "AUTOMOTIVE_DOWNLOAD_GENERATE_SIZE",
	AUTOMOTIVE_DOWNLOAD_ASPECT_RATIO = "AUTOMOTIVE_DOWNLOAD_ASPECT_RATIO",
	AUTOMOTIVE_DOWNLOAD_CUSTOM_SIZE = "AUTOMOTIVE_DOWNLOAD_CUSTOM_SIZE",
	AUTOMOTIVE_DOWNLOAD_MEDIUM_DESTINATION = "AUTOMOTIVE_DOWNLOAD_MEDIUM_DESTINATION",
	AUTOMOTIVE_DOWNLOAD_FOREGROUND = "AUTOMOTIVE_DOWNLOAD_FOREGROUND",
	AUTOMOTIVE_INCREASE_RESOLUTION = "AUTOMOTIVE_INCREASE_RESOLUTION",
	AUTOMOTIVE_ERASE = "AUTOMOTIVE_ERASE",
	AUTOMOTIVE_CROP = "AUTOMOTIVE_CROP",
	AUTOMOTIVE_GENERATIVE_FILL = "AUTOMOTIVE_GENERATIVE_FILL",
	AUTOMOTIVE_TIRES_REFINEMENT = "AUTOMOTIVE_TIRES_REFINEMENT",
	AUTOMOTIVE_REFLECTIONS = "AUTOMOTIVE_REFLECTIONS",
	AUTOMOTIVE_EFFECTS = "AUTOMOTIVE_EFFECTS",
	AUTOMOTIVE_HARMONIZATION = "AUTOMOTIVE_HARMONIZATION",
	DOWNLOAD_TG_MODEL_SOURCE = "DOWNLOAD_TG_MODEL_SOURCE",
	TG_MODEL_TRAINING_STARTED = "TG_MODEL_TRAINING_STARTED",
	// Tailored Generation Integration Events
	TAILORED_GENERATION_FIGMA_TAB_CLICK = "TAILORED_GENERATION_FIGMA_TAB_CLICK",
	TAILORED_GENERATION_FIGMA_PLUGIN_DOWNLOAD_CLICK = "TAILORED_GENERATION_FIGMA_PLUGIN_DOWNLOAD_CLICK",
	TAILORED_GENERATION_FIGMA_VIDEO_EXAMPLE_CLICK = "TAILORED_GENERATION_FIGMA_VIDEO_EXAMPLE_CLICK",
	// Image Generation Integration Events
	IMAGE_GENERATION_MCP_SIDEBAR_CLICK = "IMAGE_GENERATION_MCP_SIDEBAR_CLICK",
	IMAGE_GENERATION_MCP_TAB_CLICK = "IMAGE_GENERATION_MCP_TAB_CLICK",
	IMAGE_GENERATION_MCP_COPY_URL_CLICK = "IMAGE_GENERATION_MCP_COPY_URL_CLICK",
	IMAGE_GENERATION_MCP_SERVER_TO_SERVER_TAB_CLICK = "IMAGE_GENERATION_MCP_SERVER_TO_SERVER_TAB_CLICK",
	IMAGE_GENERATION_MCP_QUICK_TESTING_TAB_CLICK = "IMAGE_GENERATION_MCP_QUICK_TESTING_TAB_CLICK",
	IMAGE_GENERATION_MCP_CREATE_API_TOKEN_CLICK = "IMAGE_GENERATION_MCP_CREATE_API_TOKEN_CLICK",
	IMAGE_GENERATION_MCP_COPY_API_TOKEN_CLICK = "IMAGE_GENERATION_MCP_COPY_API_TOKEN_CLICK",
	IMAGE_GENERATION_MCP_VIDEO_EXAMPLE_CLICK = "IMAGE_GENERATION_MCP_VIDEO_EXAMPLE_CLICK",
	IMAGE_GENERATION_MCP_API_TOKEN_DOCS_CLICK = "IMAGE_GENERATION_MCP_API_TOKEN_DOCS_CLICK",
	IMAGE_GENERATION_MCP_BEARER_AUTH_DOCS_CLICK = "IMAGE_GENERATION_MCP_BEARER_AUTH_DOCS_CLICK",
	IMAGE_GENERATION_COMFYUI_SIDEBAR_CLICK = "IMAGE_GENERATION_COMFYUI_SIDEBAR_CLICK",
	IMAGE_GENERATION_COMFYUI_TAB_CLICK = "IMAGE_GENERATION_COMFYUI_TAB_CLICK",
	IMAGE_GENERATION_COMFYUI_COPY_API_TOKEN_CLICK = "IMAGE_GENERATION_COMFYUI_COPY_API_TOKEN_CLICK",
	IMAGE_GENERATION_COMFYUI_NODE_CLICK = "IMAGE_GENERATION_COMFYUI_NODE_CLICK",
	IMAGE_GENERATION_COMFYUI_WORKFLOW_CLICK = "IMAGE_GENERATION_COMFYUI_WORKFLOW_CLICK",
	IMAGE_GENERATION_FAL_CLICK = "IMAGE_GENERATION_FAL_CLICK",
	IMAGE_GENERATION_INVOKE_CLICK = "IMAGE_GENERATION_INVOKE_CLICK",
	IMAGE_GENERATION_AWS_CLICK = "IMAGE_GENERATION_AWS_CLICK",
	IMAGE_GENERATION_AZURE_CLICK = "IMAGE_GENERATION_AZURE_CLICK",
	IMAGE_GENERATION_FIGMA_CLICK = "IMAGE_GENERATION_FIGMA_CLICK",
	IMAGE_GENERATION_FIGMA_TAB_CLICK = "IMAGE_GENERATION_FIGMA_TAB_CLICK",
	IMAGE_GENERATION_FIGMA_PLUGIN_DOWNLOAD_CLICK = "IMAGE_GENERATION_FIGMA_PLUGIN_DOWNLOAD_CLICK",
	IMAGE_GENERATION_FIGMA_VIDEO_EXAMPLE_CLICK = "IMAGE_GENERATION_FIGMA_VIDEO_EXAMPLE_CLICK",
	IMAGE_GENERATION_PHOTOSHOP_CLICK = "IMAGE_GENERATION_PHOTOSHOP_CLICK",
	IMAGE_GENERATION_FIGMA_PLUGIN_DOWNLOAD = "IMAGE_GENERATION_FIGMA_PLUGIN_DOWNLOAD",
	IMAGE_GENERATION_PHOTOSHOP_GO_TO_MARKETPLACE = "IMAGE_GENERATION_PHOTOSHOP_GO_TO_MARKETPLACE",
	// Image Editing Integration Events
	IMAGE_EDITING_MCP_SIDEBAR_CLICK = "IMAGE_EDITING_MCP_SIDEBAR_CLICK",
	IMAGE_EDITING_MCP_TAB_CLICK = "IMAGE_EDITING_MCP_TAB_CLICK",
	IMAGE_EDITING_FIGMA_CLICK = "IMAGE_EDITING_FIGMA_CLICK",
	IMAGE_EDITING_FIGMA_TAB_CLICK = "IMAGE_EDITING_FIGMA_TAB_CLICK",
	IMAGE_EDITING_FIGMA_PLUGIN_DOWNLOAD_CLICK = "IMAGE_EDITING_FIGMA_PLUGIN_DOWNLOAD_CLICK",
	IMAGE_EDITING_FIGMA_VIDEO_EXAMPLE_CLICK = "IMAGE_EDITING_FIGMA_VIDEO_EXAMPLE_CLICK",
	IMAGE_EDITING_PHOTOSHOP_CLICK = "IMAGE_EDITING_PHOTOSHOP_CLICK",
	IMAGE_EDITING_FIGMA_PLUGIN_DOWNLOAD = "IMAGE_EDITING_FIGMA_PLUGIN_DOWNLOAD",
	IMAGE_EDITING_PHOTOSHOP_GO_TO_MARKETPLACE = "IMAGE_EDITING_PHOTOSHOP_GO_TO_MARKETPLACE",
	IMAGE_EDITING_COMFYUI_SIDEBAR_CLICK = "IMAGE_EDITING_COMFYUI_SIDEBAR_CLICK",
	IMAGE_EDITING_COMFYUI_TAB_CLICK = "IMAGE_EDITING_COMFYUI_TAB_CLICK",
	IMAGE_EDITING_COMFYUI_COPY_API_TOKEN_CLICK = "IMAGE_EDITING_COMFYUI_COPY_API_TOKEN_CLICK",
	// Tailored Generation Integration Events
	TAILORED_GENERATION_FIGMA_CLICK = "TAILORED_GENERATION_FIGMA_CLICK",
	TAILORED_GENERATION_PHOTOSHOP_CLICK = "TAILORED_GENERATION_PHOTOSHOP_CLICK",
	TAILORED_GENERATION_FIGMA_PLUGIN_DOWNLOAD = "TAILORED_GENERATION_FIGMA_PLUGIN_DOWNLOAD",
	TAILORED_GENERATION_PHOTOSHOP_GO_TO_MARKETPLACE = "TAILORED_GENERATION_PHOTOSHOP_GO_TO_MARKETPLACE",
	TAILORED_GENERATION_COMFYUI_SIDEBAR_CLICK = "TAILORED_GENERATION_COMFYUI_SIDEBAR_CLICK",
	IMAGE_EDITING_COMFYUI_NODE_CLICK = "IMAGE_EDITING_COMFYUI_NODE_CLICK",
	IMAGE_EDITING_COMFYUI_WORKFLOW_CLICK = "IMAGE_EDITING_COMFYUI_WORKFLOW_CLICK",
	IMAGE_EDITING_MCP_COPY_URL_CLICK = "IMAGE_EDITING_MCP_COPY_URL_CLICK",
	IMAGE_EDITING_MCP_SERVER_TO_SERVER_TAB_CLICK = "IMAGE_EDITING_MCP_SERVER_TO_SERVER_TAB_CLICK",
	IMAGE_EDITING_MCP_QUICK_TESTING_TAB_CLICK = "IMAGE_EDITING_MCP_QUICK_TESTING_TAB_CLICK",
	IMAGE_EDITING_MCP_CREATE_API_TOKEN_CLICK = "IMAGE_EDITING_MCP_CREATE_API_TOKEN_CLICK",
	IMAGE_EDITING_MCP_COPY_API_TOKEN_CLICK = "IMAGE_EDITING_MCP_COPY_API_TOKEN_CLICK",
	IMAGE_EDITING_MCP_VIDEO_EXAMPLE_CLICK = "IMAGE_EDITING_MCP_VIDEO_EXAMPLE_CLICK",
	IMAGE_EDITING_MCP_API_TOKEN_DOCS_CLICK = "IMAGE_EDITING_MCP_API_TOKEN_DOCS_CLICK",
	IMAGE_EDITING_MCP_BEARER_AUTH_DOCS_CLICK = "IMAGE_EDITING_MCP_BEARER_AUTH_DOCS_CLICK",
}

export interface IAnalyticsStore {
	setUser(currentUser: User | null, registrationSource?: string): Promise<void>;

	logRegistrationEvent(): void;

	logEvent(eventName: ANALYTICS_EVENTS, eventParams?: { [key: string]: any }): void;

	logImageToImageEvent(eventName: ANALYTICS_EVENTS, image: PlaygroundImage): void;
}

export default class AnalyticsStore implements IAnalyticsStore {
	rootStore: IRootStore;
	crmService = new CrmService();

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	setHubspotUser = async (currentUser: User | null, _registrationSource?: string) => {
		if (isMigrationIframe()) return;
		if (!iframeStore.isIframe()) {
			const _hsq = ((window as any)._hsq = (window as any)._hsq || []);
			if (currentUser) {
				await this.crmService.upsertContact();
				_hsq.push([
					"identify",
					{
						email: currentUser.email,
					},
				]);
			} else {
				_hsq.push(["identify", {}]);
				_hsq.push(["resetCookies"]);
			}
		}
	};

	isInternalUser = (currentUser?: User | null) => {
		return (currentUser ?? this.rootStore.authStore.user)?.email?.includes("@bria.ai") ?? false;
	};

	setUser = async (currentUser: User | null, registrationSource?: string) => {
		if (currentUser) {
			const isInternalUser = this.isInternalUser(currentUser);
			if (!isInternalUser) {
				setAnalyticsCollectionEnabled(firebaseAnalytics, isCloudEnv());
				this.logEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
					page_location: window.location.href,
					page_title: document.title,
				});
			}
			setUserProperties(firebaseAnalytics, {
				internal_user: isInternalUser ? "yes" : "no",
				role: currentUser.role,
				company: currentUser.company,
				user_domain: currentUser.email.split("@").pop(),
				user_id: currentUser.uid,
			});
			setUserId(firebaseAnalytics, currentUser.uid);
			await this.setHubspotUser(currentUser, registrationSource);
		} else {
			setAnalyticsCollectionEnabled(firebaseAnalytics, false);
			setUserId(firebaseAnalytics, null);
			await this.setHubspotUser(null);
		}
	};

	logRegistrationEvent = () => {
		this.logEvent(ANALYTICS_EVENTS.REGISTER);
		(window as any).lintrk("track", {
			conversion_id: 13339457,
		});
	};

	logEvent = (eventName: ANALYTICS_EVENTS, eventParams: { [key: string]: any } = {}) => {
		const isInternal = this.isInternalUser();
		logEvent(firebaseAnalytics, eventName.toString(), {
			platformUserId: this.rootStore.authStore.user?.uid,
			platformOrgId: getSelectedOrganization()?.organization.uid,
			platformOrgName: getSelectedOrganization()?.organization.name,
			traffic_type: isInternal ? "internal" : "external",
			...eventParams,
		});
	};

	logImageToImageEvent = (eventName: ANALYTICS_EVENTS, image: PlaygroundImage) => {
		this.logEvent(eventName, {
			itiSessionId: image?.sessionId ?? "",
			tailored_style: (image.config as ImageToImageConfigType)?.style?.tailored_style,
			sub_style: (image.config as ImageToImageConfigType)?.style?.sub_style,
		});
	};
}
